// Catalogo unico de permissoes e mapa papel -> permissoes (docs/PERMISSOES.md, secao 2).
// Lido pela API (guard e projecao de resposta) e pelo web (menu e botoes). A tela so esconde; quem nega e a API.
// Nao existe permissao negativa: "nao ve" e a falta da permissao. Varios papeis na mesma empresa = UNIAO.

export const PAPEIS = [
  'SUPERADMIN',
  'ADMIN_TENANT',
  'CONSULTOR',
  'ADMIN_EMPRESA',
  'FINANCEIRO',
  'ORCAMENTISTA',
  'VENDEDOR',
  'REPRESENTANTE',
  'PCP',
  'RH',
  'LEITURA',
] as const;
export type Papel = (typeof PAPEIS)[number];

export const PERMISSOES = [
  'custo.ver',
  'preco.ver',
  'precificacao.simular',
  'precificacao.gravar',
  'lista.ver',
  'praticado.gravar',
  'margens.ver',
  'orcamento.ver',
  'orcamento.verTodos',
  'orcamento.editar',
  'cliente.ver',
  'cliente.editar',
  'item.ver',
  'item.editar',
  'tributo.editar',
  'custoAquisicao.editar',
  'ficha.ver',
  'ficha.editar',
  'tabela.ver',
  'tabela.editar',
  'empresa.ver',
  'empresa.editar',
  'parametros.ver',
  'parametros.editar',
  'centro.ver',
  'centro.editar',
  'cpm.ver',
  'cpm.editar',
  'lote.ver',
  'lote.decidir',
  'marca.dispensar',
  'onboarding.executar',
  'usuarios.gerenciar',
  'auditoria.ver',
  'rh.ver',
  'rh.editar',
  'rateio.ver',
  'rateio.editar',
  'pessoal.ver',
  'lucro.ver',
  'carteira.ver',
  'carteira.verTodos',
  'carteira.editar',
  'plano.ver',
  'plano.editar',
  'empresas.gerenciar',
  'perfis.gerenciar',
  /** Sessao 37: tela Tenants (Configuracoes) e rotas /super/*: SO o SUPERADMIN. Nunca entra em perfil proprio. */
  'tenants.gerenciar',
  /** Sessao 46: Configuracoes › Integracoes (conexao com o ERP, rotinas e execucoes). Reservada: perfil proprio nao concede. */
  'integracoes.gerenciar',
] as const;
export type Permissao = (typeof PERMISSOES)[number];

/** Permissoes dos papeis de gestao: tudo, exceto as de RH (so RH, SUPERADMIN, ADMIN_TENANT, CONSULTOR e ADMIN_EMPRESA). */
const TUDO: readonly Permissao[] = PERMISSOES;
/** Criar grupo/empresa, desativar e convidar o primeiro administrador: so SUPERADMIN e ADMIN_TENANT (Sessao 18). */
const TUDO_SEM_EMPRESAS: readonly Permissao[] = PERMISSOES.filter((p) => p !== 'empresas.gerenciar' && p !== 'tenants.gerenciar');
/** ADMIN_TENANT: tudo, menos o que atravessa tenants (so o SUPERADMIN). */
const TUDO_SEM_TENANTS: readonly Permissao[] = PERMISSOES.filter((p) => p !== 'tenants.gerenciar');

const FINANCEIRO: readonly Permissao[] = [
  'custo.ver', 'preco.ver', 'lucro.ver', 'precificacao.simular', 'precificacao.gravar', 'lista.ver', 'praticado.gravar',
  'margens.ver', 'orcamento.ver', 'orcamento.verTodos',
  'cliente.ver', 'item.ver', 'tributo.editar', 'custoAquisicao.editar', 'ficha.ver',
  'tabela.ver', 'tabela.editar', 'empresa.ver', 'parametros.ver', 'parametros.editar',
  'centro.ver', 'cpm.ver', 'cpm.editar', 'lote.ver', 'lote.decidir', 'marca.dispensar', 'onboarding.executar',
  'rateio.ver', 'rateio.editar',
  'carteira.ver', 'carteira.verTodos', 'plano.ver', 'plano.editar',
];

const ORCAMENTISTA: readonly Permissao[] = [
  'custo.ver', 'preco.ver', 'lucro.ver', 'precificacao.simular', 'lista.ver', 'margens.ver',
  'orcamento.ver', 'orcamento.verTodos', 'orcamento.editar',
  'cliente.ver', 'cliente.editar', 'item.ver', 'ficha.ver', 'tabela.ver',
  'empresa.ver', 'parametros.ver', 'centro.ver', 'cpm.ver', 'lote.ver',
  'carteira.ver', 'carteira.verTodos', 'carteira.editar',
];

const VENDEDOR: readonly Permissao[] = [
  'preco.ver', 'lucro.ver', 'lista.ver', 'orcamento.ver', 'orcamento.editar',
  'cliente.ver', 'cliente.editar', 'item.ver', 'tabela.ver', 'empresa.ver',
  'carteira.ver', 'carteira.editar',
];

/** Representante externo (Sessao 16): como o VENDEDOR, mas SO a cor da barra: sem `lucro.ver` (lucro %, MC %, meta, minimo, barra) em nenhuma saida. */
const REPRESENTANTE: readonly Permissao[] = VENDEDOR.filter((p) => p !== 'lucro.ver');

const PCP: readonly Permissao[] = [
  'item.ver', 'item.editar', 'ficha.ver', 'ficha.editar', 'empresa.ver', 'centro.ver', 'centro.editar', 'rateio.ver',
  'carteira.ver', 'carteira.verTodos',
];

const RH: readonly Permissao[] = ['empresa.ver', 'rh.ver', 'rh.editar'];

const LEITURA: readonly Permissao[] = [
  'pessoal.ver', 'preco.ver', 'lucro.ver', 'lista.ver', 'margens.ver', 'orcamento.ver', 'orcamento.verTodos',
  'cliente.ver', 'item.ver', 'tabela.ver', 'empresa.ver',
  'carteira.ver', 'carteira.verTodos',
];

/** Mapa papel -> permissoes (fonte unica). SA, AT, CO e AE tem a mesma linha; diferem so no escopo e no teto de convite. */
export const PERMISSOES_POR_PAPEL: Readonly<Record<Papel, readonly Permissao[]>> = {
  SUPERADMIN: TUDO,
  ADMIN_TENANT: TUDO_SEM_TENANTS,
  CONSULTOR: TUDO_SEM_EMPRESAS,
  ADMIN_EMPRESA: TUDO_SEM_EMPRESAS,
  FINANCEIRO,
  ORCAMENTISTA,
  VENDEDOR,
  REPRESENTANTE,
  PCP,
  RH,
  LEITURA,
};

export function ehPapel(valor: unknown): valor is Papel {
  return typeof valor === 'string' && (PAPEIS as readonly string[]).includes(valor);
}

export function ehPermissao(valor: unknown): valor is Permissao {
  return typeof valor === 'string' && (PERMISSOES as readonly string[]).includes(valor);
}

/** Uniao das permissoes dos papeis (ordem estavel do catalogo, sem repeticao). */
export function permissoesDosPapeis(papeis: Iterable<Papel>): Permissao[] {
  const uniao = new Set<Permissao>();
  for (const p of papeis) for (const perm of PERMISSOES_POR_PAPEL[p]) uniao.add(perm);
  return PERMISSOES.filter((p) => uniao.has(p));
}

export function papelTem(papel: Papel, permissao: Permissao): boolean {
  return PERMISSOES_POR_PAPEL[papel].includes(permissao);
}

/** Papel sozinho nao tem a permissao, mas a uniao dos papeis tem. */
export function papeisTem(papeis: Iterable<Papel>, permissao: Permissao): boolean {
  for (const p of papeis) if (papelTem(p, permissao)) return true;
  return false;
}

/** Papeis de nivel plataforma/tenant/consultoria (escopo sem empresa fixa, PERMISSOES secao 3.1). */
export const PAPEIS_DE_EMPRESA: readonly Papel[] = ['ADMIN_EMPRESA', 'FINANCEIRO', 'ORCAMENTISTA', 'VENDEDOR', 'REPRESENTANTE', 'PCP', 'RH', 'LEITURA'];

/** Acesso de um usuario: papel + onde vale (tenant inteiro, grupo ou empresa). */
export interface AcessoDto {
  papel: Papel;
  empresaId: string | null;
  grupoId: string | null;
  /** Perfil proprio da empresa (secao 11 do PERMISSOES.md): quando presente, `permissoes` substitui as do papel. */
  perfilId?: string | null;
  permissoes?: readonly Permissao[];
}

/** Permissoes efetivas de um conjunto de acessos: uniao, cada acesso valendo o perfil proprio ou o padrao do papel. */
export function permissoesDosAcessos(acessos: Iterable<Pick<AcessoDto, 'papel' | 'permissoes'>>): Permissao[] {
  const uniao = new Set<Permissao>();
  for (const a of acessos) {
    // Perfil proprio nunca concede permissao reservada (defesa em profundidade: a API ja nao deixa marca-la, mas um perfil gravado por fora nao vale).
    for (const perm of a.permissoes ? a.permissoes.filter((p) => !PERMISSOES_RESERVADAS.includes(p)) : PERMISSOES_POR_PAPEL[a.papel]) uniao.add(perm);
  }
  return PERMISSOES.filter((p) => uniao.has(p));
}

/** Papeis que servem de base para um perfil proprio (nunca os administrativos). */
export const PAPEIS_BASE_DE_PERFIL: readonly Papel[] = ['FINANCEIRO', 'ORCAMENTISTA', 'VENDEDOR', 'REPRESENTANTE', 'PCP', 'RH', 'LEITURA'];

/** Nunca entram num perfil proprio: quem administra acesso e administrador padrao. */
export const PERMISSOES_RESERVADAS: readonly Permissao[] = ['usuarios.gerenciar', 'perfis.gerenciar', 'empresas.gerenciar', 'tenants.gerenciar', 'integracoes.gerenciar'];
